const DEFAULT_SUMMARY_WINDOW_MS = 30 * 60 * 1000;
const SUMMARY_DATA_SOURCE_CHAT = "chat";
const SUMMARY_DATA_SOURCE_TRANSCRIPTION = "transcription";
const SUMMARY_DATA_SOURCE_PRIVATE_CHAT = "private_chat";
const SUMMARY_DATA_SOURCE_AGENT_CHAT = "agent_chat";
export const DEFAULT_SUMMARY_DATA_SOURCES = [SUMMARY_DATA_SOURCE_CHAT, SUMMARY_DATA_SOURCE_TRANSCRIPTION];
export const SUMMARY_DATA_SOURCE_OPTIONS = [
  SUMMARY_DATA_SOURCE_CHAT,
  SUMMARY_DATA_SOURCE_PRIVATE_CHAT,
  SUMMARY_DATA_SOURCE_AGENT_CHAT,
  SUMMARY_DATA_SOURCE_TRANSCRIPTION
];

function normalizeBaseUrl(baseUrl) {
  return typeof baseUrl === "string" ? baseUrl.trim().replace(/\/+$/, "") : "";
}

function normalizePath(path) {
  return typeof path === "string" ? path.trim().replace(/^\/+/, "") : "";
}

function stableHash(input) {
  const value = String(input || "");
  let hash = 2166136261;

  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return (hash >>> 0).toString(36);
}

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeRoomUserId(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  return normalizeText(value);
}

function browserLocale() {
  return typeof navigator !== "undefined" ? normalizeText(navigator.language) : "";
}

export function buildRoomApiUrl(baseApiUrl, path, query) {
  const baseUrl = normalizeBaseUrl(baseApiUrl);
  const normalizedPath = normalizePath(path);

  if (!baseUrl || !normalizedPath) {
    return null;
  }

  const url = `${baseUrl}/${normalizedPath}`;
  if (!query || Object.keys(query).length === 0) {
    return url;
  }

  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return;
    }
    params.set(key, String(value));
  });

  const queryString = params.toString();
  return queryString ? `${url}?${queryString}` : url;
}

export function isRoomPersistenceConfigured({ persistenceApiUrl } = {}) {
  return Boolean(normalizeBaseUrl(persistenceApiUrl));
}

export function isSummaryGenerationConfigured({ openAiApiUrl, persistenceApiKey } = {}) {
  return Boolean(normalizeBaseUrl(openAiApiUrl) && persistenceApiKey);
}

export function resolveRoomUserId({ store } = {}) {
  return (
    normalizeRoomUserId(store?.state?.authUser?.id) ||
    normalizeRoomUserId(store?.state?.profile?.id) ||
    null
  );
}

export function resolveRoomDisplayName({ hubChannel, store, mySessionId } = {}) {
  const presenceName = normalizeText(
    mySessionId ? hubChannel?.presence?.state?.[mySessionId]?.metas?.[0]?.profile?.displayName : ""
  );
  return presenceName || normalizeText(store?.state?.profile?.displayName) || null;
}

export function shouldMirrorPublicChat({ type = "chat", body } = {}) {
  if (type !== "chat") {
    return false;
  }

  if (typeof body === "string") {
    const text = body.trim();
    return Boolean(text && !text.startsWith("/"));
  }

  if (!body || typeof body !== "object" || body.privacyType) {
    return false;
  }

  const text = normalizeText(body.text || body.body || body.message);
  return Boolean(text && !text.startsWith("/"));
}

export function createChatMessageIdempotencyKey({ roomId, senderSessionId, text, createdAt } = {}) {
  const stablePrefix = [roomId || "room", senderSessionId || "anonymous", createdAt || ""]
    .map(part => String(part).replace(/[:\s]+/g, "_"))
    .join(":");
  return `${stablePrefix}:${stableHash(text)}`;
}

export function buildChatMessagePayload({
  roomId,
  text,
  senderUserId,
  senderSessionId,
  senderName,
  language = null,
  createdAt = new Date().toISOString(),
  metadata
} = {}) {
  const normalizedText = normalizeText(text);
  const payload = {
    roomId,
    senderUserId: senderUserId || null,
    senderSessionId: senderSessionId || null,
    senderName: senderName || null,
    text: normalizedText,
    language,
    idempotencyKey: createChatMessageIdempotencyKey({
      roomId,
      senderSessionId,
      text: normalizedText,
      createdAt
    }),
    createdAt
  };

  if (metadata) {
    payload.metadata = metadata;
  }

  return payload;
}

function normalizeSummaryDataSourceList(dataSources) {
  if (!Array.isArray(dataSources)) {
    return [];
  }

  return [...new Set(dataSources.filter(source => SUMMARY_DATA_SOURCE_OPTIONS.includes(source)))];
}

export function sanitizeSummaryDataSources(dataSources, allowedSources) {
  const allowed = normalizeSummaryDataSourceList(allowedSources);
  const effectiveAllowed = allowed.length > 0 ? allowed : SUMMARY_DATA_SOURCE_OPTIONS;
  const requested = Array.isArray(dataSources)
    ? normalizeSummaryDataSourceList(dataSources)
    : DEFAULT_SUMMARY_DATA_SOURCES;
  const filtered = requested.filter(source => effectiveAllowed.includes(source));
  const fallback = DEFAULT_SUMMARY_DATA_SOURCES.filter(source => effectiveAllowed.includes(source));

  if (filtered.length > 0) {
    return filtered;
  }

  return fallback.length > 0 ? fallback : [...effectiveAllowed];
}

export function buildSummaryRequestPayload({
  roomId,
  from,
  to,
  windowMs = DEFAULT_SUMMARY_WINDOW_MS,
  dataSources = DEFAULT_SUMMARY_DATA_SOURCES,
  workspaceId,
  summaryAgentUuid,
  requestedByUserId,
  requestedOutputLanguage,
  downloadFormat,
  timezone,
  locale,
  metadata
} = {}) {
  const endDate = to ? new Date(to) : new Date();
  const startDate = from ? new Date(from) : new Date(endDate.getTime() - windowMs);

  return {
    roomId,
    ...(workspaceId ? { workspaceId } : {}),
    from: startDate.toISOString(),
    to: endDate.toISOString(),
    dataSources: sanitizeSummaryDataSources(dataSources),
    ...(summaryAgentUuid ? { summaryAgentUuid } : {}),
    requestedByUserId: requestedByUserId || null,
    requestedOutputLanguage: requestedOutputLanguage || null,
    ...(downloadFormat ? { downloadFormat } : {}),
    timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    locale: locale || browserLocale() || null,
    metadata: {
      client: "onland-metaverse",
      ...(metadata || {}),
      summaryInputPolicy: "server_compaction"
    }
  };
}
